import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { appPages, authPages } from '../config/pages.config';
import useLocalStorage from '../hooks/useLocalStorage';
import { plan, usePlanAPI, useUserAPI } from '../services/userService';
import { ApiReloadProvider } from './apiRelaod';


export interface IAuthContextProps {
	token: string | ((newValue: string | null) => void) | null;
	onLogin: (token: string) => Promise<void>;
	onLogout: (isMutipleLogin?: number) => void;
	userData: any;
	isLoading: boolean;
	planData: plan | undefined;
}
const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthProviderProps {
	children: ReactNode;
}
export const AuthProvider: FC<IAuthProviderProps> = ({ children }) => {
	const [ token, setToken ] = useLocalStorage('token', null);

	const { userData, error, isLoading, setIsLoading } = useUserAPI(token as string);
	const { planData, error: planError, isLoading: planLoading, setIsLoading: planSetIsLoading } = usePlanAPI(token as string);

	const [ goToLogin, setGoToLogin ] = useState(false);

	const navigate = useNavigate();

	// call this function when you want to authenticate the user
	const onLogin = async (token: string) => {
		if (token && setToken instanceof Function) {
			await setToken(token).then(() => {
				setGoToLogin(true);
				setIsLoading(true);
			});
		}
	};

	useEffect(() => {
		if (token && error) {
			onLogout();
			return;
		}
		if (!goToLogin) return;
		if(userData && !error){
			navigate('/app', { replace: true })
		} else if(!isLoading) {
			navigate(`../${authPages.loginPage.to}`, { replace: true })
		}
		if ((userData || error) && isLoading) {
			setIsLoading(false);
		}
	}, [userData, error, isLoading, token])

	// call this function to sign out logged-in user
	const onLogout = async (isMutipleLogin?: number) => {
		if (setToken instanceof Function) await setToken(null);

		const toastId = 409;
		isMutipleLogin === 409? ((!toast.isActive(toastId)) ?
		toast.success('Logged in on another device. You’ve been logged out here. Only one device can be used at a time.',{toastId}):""):
		toast.success('You have been logged out.');
		navigate(`../${authPages.loginPage.to}`, { replace: true });
	};

	const value: IAuthContextProps = useMemo(
		() => ({
			token,
			onLogin,
			onLogout,
			userData,
			isLoading,
			planData
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[token, userData, planData],
	);
	return <AuthContext.Provider value={value}>
			<ApiReloadProvider>
				{children}
			</ApiReloadProvider>
		</AuthContext.Provider>;
};

export const useAuth = () => {
	return useContext(AuthContext);
};
